import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type {
  CreateableMentionableEntityType,
  MentionableEntity,
} from '../components/RichTextField';
import { useOptionalProjectAccess } from './ProjectAccessContext';

export interface OpenCreateEntityOptions {
  documentId?: string;
  projectId?: string;
  onCreated?: (entity: MentionableEntity) => void;
}

export interface CreateEntityRequest {
  type: CreateableMentionableEntityType;
  name: string;
  documentId?: string;
  projectId?: string;
  onCreated?: (entity: MentionableEntity) => void;
}

interface CreateEntityFromMentionValue {
  openCreateEntity: (type: CreateableMentionableEntityType, name: string, options?: OpenCreateEntityOptions) => void;
  closeCreateEntity: () => void;
  rememberCreatedEntity: (entity: MentionableEntity) => void;
  getCreateableTypes: (fallbackDocumentId?: string) => CreateableMentionableEntityType[];
  request: CreateEntityRequest | null;
  createdEntities: MentionableEntity[];
}

const CreateEntityFromMentionContext = createContext<CreateEntityFromMentionValue | null>(null);

function documentScopedTypes(hasDocumentId: boolean): CreateableMentionableEntityType[] {
  if (!hasDocumentId) return [];
  return ['character', 'location', 'manual_chapter', 'storyline', 'scene'];
}

function projectScopedTypes(hasProjectId: boolean): CreateableMentionableEntityType[] {
  if (!hasProjectId) return [];
  return ['resource', 'timeline_item', 'document'];
}

export const CreateEntityFromMentionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const params = useParams<{ projectId?: string; documentId?: string }>();
  const projectAccess = useOptionalProjectAccess();
  const routeProjectId = params.projectId && params.projectId !== 'new' ? params.projectId : undefined;
  const routeDocumentId = params.documentId && params.documentId !== 'new' ? params.documentId : undefined;
  const canEdit = projectAccess ? projectAccess.canEditProject : true;
  const [request, setRequest] = useState<CreateEntityRequest | null>(null);
  const [createdEntities, setCreatedEntities] = useState<MentionableEntity[]>([]);

  const getCreateableTypes = useCallback(
    (fallbackDocumentId?: string): CreateableMentionableEntityType[] => {
      if (!canEdit) return [];
      const documentId = routeDocumentId ?? fallbackDocumentId;
      return [
        ...documentScopedTypes(Boolean(documentId)),
        ...projectScopedTypes(Boolean(routeProjectId)),
      ];
    },
    [canEdit, routeDocumentId, routeProjectId],
  );

  const openCreateEntity = useCallback(
    (type: CreateableMentionableEntityType, name: string, options?: OpenCreateEntityOptions) => {
      setRequest({
        type,
        name,
        documentId: options?.documentId ?? routeDocumentId,
        projectId: options?.projectId ?? routeProjectId,
        onCreated: options?.onCreated,
      });
    },
    [routeDocumentId, routeProjectId],
  );

  const rememberCreatedEntity = useCallback((entity: MentionableEntity) => {
    setCreatedEntities((prev) => [entity, ...prev.filter((existing) => existing.id !== entity.id)]);
  }, []);

  const closeCreateEntity = useCallback(() => {
    setRequest(null);
  }, []);

  const value = useMemo(
    () => ({
      openCreateEntity,
      closeCreateEntity,
      rememberCreatedEntity,
      getCreateableTypes,
      request,
      createdEntities,
    }),
    [openCreateEntity, closeCreateEntity, rememberCreatedEntity, getCreateableTypes, request, createdEntities],
  );

  return (
    <CreateEntityFromMentionContext.Provider value={value}>
      {children}
    </CreateEntityFromMentionContext.Provider>
  );
};

export function useCreateEntityFromMentionOptional(): CreateEntityFromMentionValue | null {
  return useContext(CreateEntityFromMentionContext);
}
